<template>
  <div>
    <!-- 검색 중: 트리 대신 매칭된 메뉴만 평탄화하여 표시 -->
    <template v-if="isSearching">
      <v-list-item v-for="(result, index) in searchResults" :key="`dya-menu-search-${index}`" rounded="lg"
        :to="result.item.to" :href="result.item.href" :target="result.item.target"
        :style="`padding-inline-start: 12px !important`" class="!min-h-11 rounded-none"
        @click="adminMainStore.menuSearchKeyword = ''">
        <template v-if="result.item.icon" #prepend>
          <v-icon :icon="result.item.icon.prefix + ' fa-' + result.item.icon.iconName" />
        </template>
        <v-list-item-title class="mr-auto">
          {{ result.item.title }}
        </v-list-item-title>
        <v-list-item-subtitle v-if="result.path" class="!text-xs opacity-60">
          {{ result.path }}
        </v-list-item-subtitle>
      </v-list-item>

      <v-list-item v-if="!searchResults.length" class="!min-h-11 rounded-none"
        :style="`padding-inline-start: 12px !important`">
        <v-list-item-title class="!text-sm opacity-60">검색 결과가 없습니다.</v-list-item-title>
      </v-list-item>
    </template>

    <template v-else>
      <template v-for="entry in menu">
        <template v-if="hasAuth(entry)">
          <v-list-subheader v-if="entry.isHeader" class="mt-4 !text-amber-500 !min-h-11 rounded-none dya-menu-header"
            :style="`padding-inline-start: ${12 + (8 * level)}px !important`">
            <span class="dya-menu-header-text">{{ entry.title }}</span>
          </v-list-subheader>

          <v-list-item v-else-if="!entry.children || !entry.children.length" :key="entry.id" rounded="lg" :to="entry.to" :href="entry.href" :target="entry.target"
            :active="resolveActive(entry)"
            :style="`padding-inline-start: ${12 + (8 * level)}px !important`" class="!min-h-11 rounded-none">
            <template v-if="entry.icon" #prepend>
              <v-icon :icon="entry.icon.prefix + ' fa-' + entry.icon.iconName" />
            </template>
            <v-list-item-title class="mr-auto">
              {{ entry.title }}
            </v-list-item-title>
            <template v-if="entry.to || entry.href" v-slot:append>
              <v-btn v-if="!isInFavoriteMenu(entry)" color="grey-lighten-1 !px-0 !min-w-4" variant="text"
                @click.stop.prevent="addFavoriteMenu(entry)" size="small">
                <template #append>
                  <v-icon v-if="entry.icon" class="mr-1 !text-lg" icon="far fa-star" />
                </template>
              </v-btn>
              <v-btn v-else color="grey-lighten-1 !px-0 !min-w-4"  variant="text" size="small"
                @click.stop.prevent="removeFavoriteMenu(entry)">
                <template #append>
                  <v-icon v-if="entry.icon" class="mr-1 !text-lg" icon="fas fa-star" />
                </template>
              </v-btn>
            </template>
          </v-list-item>

          <v-list-group v-else>
            <template #activator="{ props }">
              <v-list-item v-bind="props" rounded="lg" :style="`padding-inline-start: ${12 + (8 * level)}px !important`" class="!min-h-11 rounded-none">
                <template v-if="entry.icon" #prepend>
                  <v-icon :icon="entry.icon.prefix + ' fa-' + entry.icon.iconName" />
                </template>
                <v-list-item-title class="mr-auto">
                  {{ entry.title }}
                </v-list-item-title>
              </v-list-item>
            </template>
            <DYAMenu :menu="entry.children" :level="level + 1" />
          </v-list-group>
        </template>
      </template>
    </template>
  </div>
</template>
<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faHome, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { useDYAuth, type AuthCheckParams } from '~/modules/nuxt3-module-doyac/composables/auth';
import { hasDYAMenuAuth, normalizeDYAMenuKeyword, searchDYAMenu } from '../composables/dya-menu-search';
import { useDYAdminMainStore } from '../stores/main';

library.add(faHome, faUsers)

export interface DYAMenuItem {
  title: string
  id?: string
  to?: string
  href?: string
  target?: '_blank'|'_self'
  icon?: IconDefinition
  auth?: string | AuthCheckParams | boolean
  children?: DYAMenuItem[]
  isHeader?: boolean
}

const props = withDefaults(defineProps<{
  menu: DYAMenuItem[],
  level?: number
}>(), {
  level: () => 0
})

const { hasAuth: _hasAuth } = useDYAuth()
const adminMainStore = useDYAdminMainStore()
const route = useRoute()

// 2026-07-27 사이드바 메뉴 검색. 검색어가 있으면 트리 대신 매칭되는 메뉴만 평탄화해서 보여준다.
// 최상위(level 0)에서만 검색 결과를 렌더링하며, 하위 DYAMenu는 v-list-group 안에서만 쓰이므로 검색 중에는 렌더링되지 않는다.
const isSearching = computed(() => props.level === 0 && normalizeDYAMenuKeyword(adminMainStore.menuSearchKeyword).length > 0)

const searchResults = computed(() => {
  if (!isSearching.value) return []
  return searchDYAMenu(props.menu, adminMainStore.menuSearchKeyword, hasAuth)
})

// 2026-04-17 쿼리스트링이 포함된 메뉴 항목(예: /sales/totalSales?totalType=partner)이
// 동일 경로의 쿼리 없는 항목(/sales/totalSales)과 함께 활성화되는 버그 대응.
// 쿼리가 있는 메뉴: 경로 + 쿼리 모두 일치해야 활성. 쿼리가 없는 메뉴: 해당 경로에 "다른 형제 메뉴의 쿼리"가 없을 때만 활성.
const resolveActive = (item: DYAMenuItem): boolean | undefined => {
  if (!item.to) return undefined
  const qIdx = item.to.indexOf('?')
  const itemPath = qIdx === -1 ? item.to : item.to.substring(0, qIdx)
  if (route.path !== itemPath) return undefined

  if (qIdx !== -1) {
    const itemQuery = new URLSearchParams(item.to.substring(qIdx + 1))
    for (const [k, v] of itemQuery) {
      if (String(route.query[k] ?? '') !== v) return false
    }
    return true
  }

  // 쿼리가 없는 메뉴: 같은 경로를 쓰는 형제 메뉴의 쿼리 키가 현재 라우트에 존재하면 비활성
  const siblingQueryKeys = collectSiblingQueryKeys(item)
  for (const k of siblingQueryKeys) {
    if (route.query[k] !== undefined) return false
  }
  return undefined
}

const collectSiblingQueryKeys = (item: DYAMenuItem): Set<string> => {
  const keys = new Set<string>()
  const itemPath = item.to?.split('?')[0]
  if (!itemPath) return keys
  const walk = (list: DYAMenuItem[]) => {
    for (const m of list) {
      if (m !== item && m.to) {
        const qIdx = m.to.indexOf('?')
        if (qIdx !== -1 && m.to.substring(0, qIdx) === itemPath) {
          const q = new URLSearchParams(m.to.substring(qIdx + 1))
          for (const [k] of q) keys.add(k)
        }
      }
      if (m.children) walk(m.children)
    }
  }
  walk(props.menu)
  return keys
}

const hasAuth = (item: DYAMenuItem): boolean => hasDYAMenuAuth(item, _hasAuth)

const addFavoriteMenu = async (item: DYAMenuItem) => {
  if (item.to) {
    await adminMainStore.addFavoriteMenu({ title: item.title, to: item.to })
  }
  if (item.href) {
    await adminMainStore.addFavoriteMenu({ title: item.title, href: item.href, target: item.target })
  }
}

const removeFavoriteMenu = async (item: DYAMenuItem) => {
  if (item.to) {
    await adminMainStore.removeFavoriteMenu({ title: item.title, to: item.to })
  }
  if (item.href) {
    await adminMainStore.removeFavoriteMenu({ title: item.title, href: item.href })
  }
}

const isInFavoriteMenu = (item: DYAMenuItem) => {
  return !!(adminMainStore.personalSetting?.favoriteMenu.some(entry => (item.to && (item.to === entry.to) || (item.href && (item.href === entry.href)))))
}
</script>

<style scoped lang="scss">
:deep(.v-list-item__spacer) {
  width: 0 !important;
}

:deep(.v-list-item__prepend > .v-icon) {
  margin-inline-end: 8px !important;
}

:deep(.v-list-item),
:deep(.v-list-subheader) {
  min-height: 44px !important;
}

:deep(.v-navigation-drawer--rail:not(.v-navigation-drawer--is-hovering)) {
  .dya-menu-header {
    padding-inline-start: 0 !important;
    justify-content: center;

    .dya-menu-header-text {
      display: none;
    }

    &::after {
      content: '...';
      display: block;
    }
  }

  .v-list-item-title,
  .v-list-item__append {
    display: none !important;
  }
}
</style>